import { createGlobalStyle } from 'styled-components';

export const GlobalStyle = createGlobalStyle`
  :root {
    --bg-primary: #F4F5F6;
    --bg-secondary: #ffffff;
    --bg-tertiary: #F4F4F4;
    --text-primary: ${props => props.theme.colors.book.color2b};
    --text-secondary: #4b5563;
    --text-tertiary: #6b7280;
    --border-primary: #e5e7eb;
    --border-secondary: #d1d5db;
    --modal-border: #ddd0ff;
    --chat-bubble-bg: #f0f0f0;
    --chat-user-bg: #FEE500;
    --chat-user-radius: 12px 12px 0 12px;
    --chat-ai-bg: #FFFFFF;
    --chat-ai-border: #E5E5E5;
    --icon-color: #374151;
    --code-bg: #f3f4f6;
    --panel-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
    --input-shadow: 0px 0px 20px 20px rgba(255,255,255,0.9);
    --quick-action-bg: #f4f4f4;
    --quick-action-hover: #e4e4e4;
    --quick-action-active-bg: #2b2b2b;
    --quick-action-active-hover: #3b3b3b;
    --light-pink: #FFF0F3;
    --message-bg: #CFFF81;
    --bg-black: #0B0B0B;
    --bg-white: ##ffffffe8;
    --pdf-folder-remove-icon: #9B9B9B;
    --pdf-folder-remove-icon-active: #ffffff;
  }

  :root.dark-mode,
  html.dark-mode,
  html.dark-mode body,
  body.dark-mode {
    --bg-primary: #1B1B1B !important;
    --bg-secondary: #0B0B0B !important;
    --bg-tertiary: #0B0B0B !important;
    --text-primary: #EBEBEB !important;
    --text-secondary: #EBEBEB !important;
    --text-tertiary: #EBEBEB !important;
    --border-primary: #3B3B3B !important;
    --border-secondary: #3B3B3B !important;
    --modal-border: #3B3B3B !important;
    --chat-bubble-bg: #D4D4D4 !important;
    --chat-user-bg: linear-gradient(92deg, rgba(221, 208, 255, 0.10) 0%, rgba(230, 234, 216, 0.10) 32.42%, rgba(211, 255, 216, 0.10) 66.78%, rgba(202, 220, 255, 0.10) 100.65%) !important;
    --chat-user-radius: 4px !important;
    --chat-ai-bg: #0B0B0B !important;
    --chat-ai-border: #3B3B3B !important;
    --icon-color: #D4D4D4 !important;
    --code-bg: #1f2937 !important;
    --panel-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.10) !important;
    --input-shadow: 0px 0px 20px 20px rgba(11,11,11,0.9) !important;
    --quick-action-bg: #2b2b2b !important;
    --quick-action-hover: #3b3b3b !important;
    --quick-action-active-bg: #D4D4D4 !important;
    --quick-action-active-hover: #c4c4c4 !important;
    --light-pink: #5b242f !important;
    --message-bg: #093D33 !important;
    --bg-black: #FFFFFF !important;
    --bg-white: #0B0B0Be8 !important;
    --pdf-folder-remove-icon: #9B9B9B !important;
    --pdf-folder-remove-icon-active: #2B2B2B !important;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    font-size: ${props => props.theme.fontSize.base};
    line-height: 1.5;
  }

  body {
    font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: var(--bg-primary);
    color: var(--text-primary);
    line-height: 1.6;
    font-size: ${props => props.theme.fontSize.base};
    font-weight: ${props => props.theme.fontWeight.normal};
    transition: background-color 0.3s ease, color 0.3s ease;
  }

  #root {
    min-height: 100vh;
  }

  /* 스크롤바 스타일링 */
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  ::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.gray[100]};
    border-radius: ${props => props.theme.borderRadius.full};
  }

  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.gray[300]};
    border-radius: ${props => props.theme.borderRadius.full};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.gray[400]};
  }

  /* 포커스 스타일 */
  *:focus {
    outline: 2px solid ${props => props.theme.colors.toss.blue};
    outline-offset: 2px;
  }

  /* 선택 텍스트 스타일 */
  ::selection {
    background-color: ${props => props.theme.colors.toss.blue};
    color: ${props => props.theme.colors.gray[900]};
  }

  /* 링크 스타일 */
  a {
    color: ${props => props.theme.colors.toss.blue};
    text-decoration: none;
    transition: color 0.2s ease;
  }

  a:hover {
    color: ${props => props.theme.colors.primary[700]};
  }

  /* 버튼 기본 스타일 리셋 */
  button {
    font-family: inherit;
    cursor: pointer;
    border: none;
    background: none;
    padding: 0;
    margin: 0;
    outline: none;
  }

  /* 입력 필드 기본 스타일 */
  input, textarea, select {
    font-family: inherit;
    font-size: inherit;
    border: none;
    outline: none;
    background: transparent;
  }

  /* 리스트 스타일 리셋 */
  ul, ol {
    list-style: none;
  }

  /* 테이블 스타일 리셋 */
  table {
    border-collapse: collapse;
    border-spacing: 0;
  }

  /* 이미지 스타일 */
  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  /* 코드 스타일 */
  code {
    font-family: 'Fira Code', 'Monaco', 'Consolas', 'Courier New', monospace;
    background-color: ${props => props.theme.colors.gray[100]};
    padding: 0.125rem 0.25rem;
    border-radius: ${props => props.theme.borderRadius.sm};
    font-size: 0.875em;
  }

  pre {
    font-family: 'Fira Code', 'Monaco', 'Consolas', 'Courier New', monospace;
    background-color: ${props => props.theme.colors.gray[100]};
    padding: ${props => props.theme.spacing.md};
    border-radius: ${props => props.theme.borderRadius.md};
    overflow-x: auto;
  }

  /* 접근성 개선 */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
`;
